import moment, { Moment } from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { QueryParamConfig, StringParam, useQueryParams, withDefault } from 'use-query-params';
import { openAlertModal } from '~/javascripts/admin/actions/alert-modal';
import { cancelCvsApplication, getCvsApplications } from '~/javascripts/admin/actions/cvs';
import { useAppDispatch, useAppSelector } from '~/javascripts/admin/hooks/useApp';
import { selectUserPrivateMoney } from '~/javascripts/admin/reducers/user-private-money';
import { toQueryParams } from '~/javascripts/admin/utils';
import FA from '../../atoms/FontAwesome';
import Headings from '../../atoms/Headings';
import UserPrivateMoneyNotAllowed from '../../atoms/UserPrivateMoneyNotAllowed';
import BreadCrumb from '../../molecules/BreadCrumb';
import SelectedPrivateMoneyDisplay from '../../organisms/SelectedPrivateMoneyDisplay';
import FilterForm from './FilterForm';
import ListTable from './ListTable';

type CvsApplications = Awaited<ReturnType<typeof getCvsApplications>>;

const PageWrapper = styled.div``;

const HeadingWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 20px 0 10px 0;
`;

const ModalWarningMessage = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: center;

  p {
    margin-bottom: 0;
    color: #d75f48;
  }
`;

const Icon = styled(FA)`
  font-size: 16px;
`;

const CRUMBS = [
  { value: 'ホーム', href: '/', isCurrentPage: false },
  { value: '取引管理', href: '', isCurrentPage: true },
  { value: 'コンビニ決済申込履歴', href: '', isCurrentPage: true },
];

const MomentParam: QueryParamConfig<Moment | null | undefined> = {
  encode: (value) => (value ? value.toISOString() : null),
  decode: (value) => (value && typeof value === 'string' ? moment(value) : null),
};

const queryParamConfigs = {
  status: withDefault(StringParam, null),
  customer_id: withDefault(StringParam, null),
  receipt_no: withDefault(StringParam, null),
  pay_limit_from: withDefault(MomentParam, null),
  pay_limit_to: withDefault(MomentParam, null),
};

const ConvenienceStoreRequests = () => {
  const dispatch = useAppDispatch();
  const { id: privateMoneyId } = useAppSelector(selectUserPrivateMoney);
  const { user } = useAppSelector((state) => ({
    user: state.user,
  }));

  const [cvsApplications, setCvsApplications] = useState<CvsApplications>();

  const isSelectedMoneyAllowed = user.isIssuerOf(privateMoneyId) || user.isServiceUser();

  const [query, setQuery] = useQueryParams({
    ...queryParamConfigs,
  });

  const [currentPaginatedQuery, setCurrentPaginatedQuery] = useState<string | null>(null);

  const fetchCvsApplications = useCallback(
    (queryLink = '') => {
      if (privateMoneyId) {
        const queryString = !!queryLink
          ? queryLink
          : toQueryParams(
              {
                ...query,
              },
              true
            );

        getCvsApplications(privateMoneyId, queryString).then((res) => setCvsApplications(res));
      }
    },
    [query, privateMoneyId]
  );

  const onChangePage = (paginatedQuery: string | null) => {
    const queryParams = paginatedQuery?.split('?')[1];
    setCurrentPaginatedQuery(queryParams ?? null);
    fetchCvsApplications(queryParams ?? undefined);
  };

  const onCancelCvs = (orderId: string) => {
    dispatch(
      openAlertModal({
        body: (
          <div>
            <p>申込の取消処理を実行してもよろしいですか?</p>
            <ModalWarningMessage>
              <Icon icon="exclamation-triangle" color="#D75F48" />
              <p>この操作は元に戻せません</p>
            </ModalWarningMessage>
          </div>
        ),
        confirmText: '実行',
        confirmColor: 'danger',
        onConfirm: () => {
          cancelCvsApplication(orderId)
            .then(() => fetchCvsApplications(currentPaginatedQuery ?? undefined))
            .catch((err) => console.error(err));
        },
      })
    );
  };

  useEffect(() => {
    fetchCvsApplications();
  }, [query, privateMoneyId]);

  return (
    <PageWrapper>
      <BreadCrumb crumbs={CRUMBS} />

      {isSelectedMoneyAllowed ? (
        <>
          <SelectedPrivateMoneyDisplay />
          <FilterForm query={query} setQuery={setQuery} />
          <HeadingWrapper>
            <Headings.h5>検索結果</Headings.h5>
          </HeadingWrapper>
          <ListTable
            cvsApplications={cvsApplications}
            onChangePage={onChangePage}
            onCancelCvs={onCancelCvs}
          />
        </>
      ) : (
        <UserPrivateMoneyNotAllowed />
      )}
    </PageWrapper>
  );
};

export default ConvenienceStoreRequests;
